import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

const base = (movementId, selectedAccount) =>
  `${apiBaseUrl}/movements/${movementId}/test_functions?account_id=${selectedAccount}`;

export const getAllMovementFunctions = createAsyncThunk(
  "movementFunctions/getAll",
  async ({ movementId, selectedAccount }, { rejectWithValue }) => {
    try {
      const res = await axios.get(base(movementId, selectedAccount), { headers: authHeaders() });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getMovementCalculationOptions = createAsyncThunk(
  "movementFunctions/getCalculationOptions",
  async ({ movementId, selectedAccount }, { rejectWithValue }) => {
    try {
      const res = await axios.get(base(movementId, selectedAccount), { headers: authHeaders() });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createMovementFunction = createAsyncThunk(
  "movementFunctions/create",
  async ({ movementId, testFunction, selectedAccount }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        base(movementId, selectedAccount),
        { test_function: testFunction },
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Display images are plain TestFunctions (data_type: "DisplayImage") created and
// image-attached in a single multipart request, so the UI never exposes the
// underlying field-builder concept to the user.
export const createDisplayImage = createAsyncThunk(
  "movementFunctions/createDisplayImage",
  async ({ movementId, imageFile, displayName, selectedAccount }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("test_function[data_type]", "DisplayImage");
      fd.append("test_function[display_name]", displayName);
      fd.append("test_function[function_name]", `display_image_${Date.now()}`);
      fd.append("test_function[image]", imageFile);
      const res = await axios.post(base(movementId, selectedAccount), fd, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${getToken()}` },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const editMovementFunction = createAsyncThunk(
  "movementFunctions/edit",
  async ({ movementId, testFunctionId, testFunction, selectedAccount }, { rejectWithValue }) => {
    try {
      await axios.put(
        `${apiBaseUrl}/movements/${movementId}/test_functions/${testFunctionId}?account_id=${selectedAccount}`,
        { test_function: testFunction },
        { headers: authHeaders() }
      );
      return { testFunctionId };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteMovementFunction = createAsyncThunk(
  "movementFunctions/delete",
  async ({ movementId, testFunctionId, selectedAccount }, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${apiBaseUrl}/movements/${movementId}/test_functions/${testFunctionId}?account_id=${selectedAccount}`,
        { headers: authHeaders() }
      );
      toast.success("Field deleted.");
      return { testFunctionId };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Copies a Test's current fields into this Movement as brand-new,
// independent TestFunction records (see CopyTestFieldsToMovementService).
// Returns the movement's full, refreshed field list.
export const copyFieldsFromTest = createAsyncThunk(
  "movementFunctions/copyFieldsFromTest",
  async ({ movementId, testId, fieldIds, selectedAccount }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${apiBaseUrl}/movements/${movementId}/copy_fields_from_test?account_id=${selectedAccount}`,
        { test_id: testId, field_ids: fieldIds },
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateMovementOrder = createAsyncThunk(
  "movementFunctions/updateOrder",
  async ({ movementId, order, selectedAccount }, { rejectWithValue }) => {
    try {
      await axios.patch(
        `${apiBaseUrl}/movements/${movementId}/update_order?account_id=${selectedAccount}`,
        { movement: { order_test_function_ids: order } },
        { headers: authHeaders() }
      );
      return { order };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const movementFunctionSlice = createSlice({
  name: "movementFunction",
  initialState: {
    functionsList: [],
    isLoading:     false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllMovementFunctions.pending,   (s) => { s.isLoading = true; })
      .addCase(getAllMovementFunctions.fulfilled, (s, a) => { s.isLoading = false; s.functionsList = a.payload; })
      .addCase(getAllMovementFunctions.rejected,  (s) => { s.isLoading = false; })
      .addCase(createMovementFunction.fulfilled,  (s, a) => { s.functionsList.push(a.payload); })
      .addCase(createDisplayImage.fulfilled,      (s, a) => { s.functionsList.push(a.payload); })
      .addCase(copyFieldsFromTest.fulfilled,      (s, a) => { s.functionsList = a.payload; })
      .addCase(deleteMovementFunction.fulfilled,  (s, a) => {
        s.functionsList = s.functionsList.filter((fn) => fn.id !== a.payload.testFunctionId);
      });
  },
});

export default movementFunctionSlice.reducer;
